import { DateWithoutTime } from 'common/models/dateType';
import { Contractor } from 'common/models/harvestData/contractor';
import { HaulLabel } from 'common/models/harvestData/hauler';
import {
  InvoiceSummaryLine,
  PayrollData,
} from 'common/models/harvestData/payrollData';
import { Pool } from 'common/models/harvestData/pool';
import { Season } from 'common/models/season';
import { OtherRate } from 'common/models/settings/otherRates';
import { SavePayrollPayload } from 'features/harvest-payroll/hooks/useSavePayroll';
import { cmsApi } from '../cmsApi';
import {
  AssignTeamPayload,
  PartialPickPayload,
  PickRecordPayload,
} from '../dto/harvestDataApi.types';
import {
  AddTransferRateDto,
  EditHaulRateDto,
  EditSurchargesDto,
  EditTransferRateDto,
  FuelSurchargeRangeDto,
  HaulRateDto,
  PickRateDto,
  TransferRateDto,
} from '../dto/rates.dto';
import { shouldForceRefetch } from '../utils/handleExpiredSession';
import { cacheIdFormatters as blockCacheFormatters } from './block';
import { cacheIdFormatters as growerCacheFormatters } from './grower';

const harvestDataBaseUrl = '/harvest-data';

export const withHarvestDataEndpoints = cmsApi.injectEndpoints({
  endpoints: builder => ({
    getSeasons: builder.query<Season[], void>({
      query: () => ({
        url: `${harvestDataBaseUrl}/seasons`,
        method: 'GET',
      }),
      providesTags: (_, err) => (err ? [] : ['Seasons']),
      forceRefetch: shouldForceRefetch,
    }),

    editSeason: builder.mutation<
      void,
      { id: number; isDefault?: boolean; isActive?: boolean }[]
    >({
      query: body => ({
        url: `${harvestDataBaseUrl}/season`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['Seasons']),
    }),

    getPools: builder.query<Pool[], { seasonId: number; varietyId?: number }>({
      query: ({ seasonId, varietyId }) => ({
        url: `${harvestDataBaseUrl}/pools/${seasonId}${
          varietyId ? `/${varietyId}` : ''
        }`,
        method: 'GET',
      }),
      providesTags: (_, err, arg) =>
        err ? [] : [{ type: 'Pools', id: `${arg.varietyId}-${arg.seasonId}` }],
      forceRefetch: shouldForceRefetch,
    }),

    editActivePools: builder.mutation<void, { id: number; active: boolean }[]>({
      query: body => ({
        url: `${harvestDataBaseUrl}/pools`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['Pools']),
    }),

    getPickRates: builder.query<PickRateDto[], void>({
      query: () => ({
        url: `${harvestDataBaseUrl}/rates/pick`,
        method: 'GET',
      }),
      providesTags: (_, err) => (err ? [] : ['PickRates']),
      forceRefetch: shouldForceRefetch,
    }),

    editPickRates: builder.mutation<void, { id: number; rate: number }[]>({
      query: body => ({
        url: `${harvestDataBaseUrl}/rates/pick`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['PickRates']),
    }),

    getHaulRates: builder.query<HaulRateDto[], { pickDate: string }>({
      query: arg => ({
        url: `${harvestDataBaseUrl}/rates/haul/${arg.pickDate}`,
        method: 'GET',
      }),
      providesTags: (_, err, arg) =>
        err ? [] : [{ type: 'HaulRates', id: arg.pickDate }],
      forceRefetch: shouldForceRefetch,
    }),

    editHaulRates: builder.mutation<void, EditHaulRateDto[]>({
      query: body => ({
        url: `${harvestDataBaseUrl}/rates/haul`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['HaulRates']),
    }),

    getTransferRates: builder.query<
      TransferRateDto[],
      {
        /**
          The Monday that starts the week to retrieve rates for.

          @remarks

          Because FSCs are effective the first Monday of the month, this expects
          the given value to be a Monday. Coupled with the fact that rates don't
          change for a month, this means that the returned rates apply regardless
          of the day of the week (i.e. no need to fetch rates for each transfer
          date).
        */
        weekStart: string;
      }
    >({
      query: arg => ({
        url: `${harvestDataBaseUrl}/rates/transfer/${arg.weekStart}`,
        method: 'GET',
      }),
      providesTags: (_, err, arg) =>
        err ? [] : [{ type: 'TransferRates', id: arg.weekStart }],
      forceRefetch: shouldForceRefetch,
    }),

    addTransferRate: builder.mutation<void, AddTransferRateDto>({
      query: body => ({
        url: `${harvestDataBaseUrl}/rates/transfer`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['TransferRates']),
    }),

    editTransferRates: builder.mutation<void, EditTransferRateDto[]>({
      query: body => ({
        url: `${harvestDataBaseUrl}/rates/transfer`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['TransferRates']),
    }),

    getSurchargesByMonth: builder.query<
      FuelSurchargeRangeDto[],
      { firstMonday: DateWithoutTime }
    >({
      query: ({ firstMonday }) => ({
        url: `${harvestDataBaseUrl}/rates/fuel-surcharges/${firstMonday}`,
        method: 'GET',
      }),
      providesTags: (_, err, args) =>
        err ? [] : [{ type: 'SurchargeRates', id: args.firstMonday }],
      forceRefetch: shouldForceRefetch,
    }),

    editSurcharges: builder.mutation<
      void,
      { firstMonday: DateWithoutTime; dto: EditSurchargesDto }
    >({
      query: ({ firstMonday, dto }) => ({
        url: `${harvestDataBaseUrl}/rates/fuel-surcharges/${firstMonday}`,
        method: 'PUT',
        body: dto,
      }),
      invalidatesTags: (_, err, args) =>
        err ? [] : [{ type: 'SurchargeRates', id: args.firstMonday }],
    }),

    getRangeLabels: builder.query<HaulLabel[], void>({
      query: () => ({
        url: `${harvestDataBaseUrl}/range`,
        method: 'GET',
      }),
      providesTags: (_, err) => (err ? [] : ['RangeLabels']),
      forceRefetch: shouldForceRefetch,
    }),

    addRange: builder.mutation<void, Pick<HaulLabel, 'miles'>>({
      query: body => ({
        url: `${harvestDataBaseUrl}/range`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['RangeLabels']),
    }),

    getOtherRates: builder.query<OtherRate[], void>({
      query: () => ({
        url: `${harvestDataBaseUrl}/rates/other`,
        method: 'GET',
      }),
      providesTags: (_, err) => (err ? [] : ['OtherRates']),
      forceRefetch: shouldForceRefetch,
    }),

    editOtherRates: builder.mutation<void, { id: number; rate: number }[]>({
      query: body => ({
        url: `${harvestDataBaseUrl}/rates/other`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['OtherRates']),
    }),

    getContractors: builder.query<Contractor[], { activeOnly: boolean } | void>(
      {
        query: activeOnly => ({
          url: `${harvestDataBaseUrl}/contractors/${!!activeOnly}`,
          method: 'GET',
        }),
        providesTags: (_, err) => (err ? [] : ['HarvestCrews']),
        forceRefetch: shouldForceRefetch,
      },
    ),

    editContractors: builder.mutation<
      void,
      Pick<Contractor, 'id' | 'vendorId' | 'active'>[]
    >({
      query: body => ({
        url: `${harvestDataBaseUrl}/contractors`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['HarvestCrews']),
    }),

    assignTeam: builder.mutation<void, AssignTeamPayload>({
      query: team => ({
        url: `${harvestDataBaseUrl}/assign-staff-and-crew`,
        method: 'PUT',
        body: team,
      }),
      invalidatesTags: (_, err, { scheduledPickId }) =>
        err ? [] : [{ type: 'ScheduledPick', id: scheduledPickId }],
    }),

    savePartialPickRecords: builder.mutation<void, PartialPickPayload[]>({
      query: body => ({
        url: `${harvestDataBaseUrl}/partial-pick-records`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err, body) =>
        err ? [] : [{ type: 'ScheduledPick', id: body[0].scheduledPickId }],
    }),

    savePickRecord: builder.mutation<void, PickRecordPayload>({
      query: data => ({
        url: `${harvestDataBaseUrl}/pick-record`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (
        _,
        err,
        { schedulePickId, seasonId, blockId, growerId },
      ) =>
        err
          ? []
          : [
              {
                type: 'ScheduledPick',
                id: schedulePickId,
              },
              {
                type: 'Block',
                id: blockCacheFormatters.block(seasonId, blockId, 'basic'),
              },
              {
                type: 'Block',
                id: blockCacheFormatters.block(seasonId, blockId, 'all'),
              },
              {
                type: 'Grower',
                id: growerCacheFormatters.grower(seasonId, growerId),
              },
              {
                type: 'RecentBlocks',
                id: blockCacheFormatters.recentBlocks(seasonId),
              },
              {
                type: 'PendingBlocks',
                id: blockCacheFormatters.pendingBlocks(seasonId),
              },
              {
                type: 'HarvestableBlocks',
                id: blockCacheFormatters.harvestableBlocks(seasonId),
              },
              {
                type: 'Growers',
                id: growerCacheFormatters.growers(seasonId),
              },
            ],
    }),

    getPayrollDataByContractor: builder.query<
      PayrollData,
      { contractorId: number; startDate: string }
    >({
      query: ({ contractorId, startDate }) => ({
        url: `${harvestDataBaseUrl}/contractor-invoice/${contractorId}/${startDate}`,
        method: 'GET',
      }),
      providesTags: (_, err) => (err ? [] : ['CtrPayroll']),
      forceRefetch: shouldForceRefetch,
    }),

    getInvoiceSummaries: builder.query<
      InvoiceSummaryLine[],
      { startDate: string }
    >({
      query: ({ startDate }) => ({
        url: `${harvestDataBaseUrl}/invoice-summary/${startDate}`,
        method: 'GET',
      }),
      // We always want to see the latest data for invoice summaries.
      forceRefetch: () => true,
    }),

    savePayroll: builder.mutation<void, SavePayrollPayload>({
      query: rows => ({
        url: `${harvestDataBaseUrl}/contractor-invoice`,
        method: 'PUT',
        body: rows,
      }),
      invalidatesTags: (_, err) => (err ? [] : ['CtrPayroll']),
    }),
  }),
});

export const {
  useGetSeasonsQuery,
  useEditSeasonMutation,
  useGetPoolsQuery,
  useEditActivePoolsMutation,
  useGetPickRatesQuery,
  useEditPickRatesMutation,
  useGetHaulRatesQuery,
  useEditHaulRatesMutation,
  useGetTransferRatesQuery,
  useAddTransferRateMutation,
  useEditTransferRatesMutation,
  useGetSurchargesByMonthQuery,
  useEditSurchargesMutation,
  useGetRangeLabelsQuery,
  useAddRangeMutation,
  useGetOtherRatesQuery,
  useEditOtherRatesMutation,
  useGetContractorsQuery,
  useEditContractorsMutation,
  useAssignTeamMutation,
  useSavePartialPickRecordsMutation,
  useSavePickRecordMutation,
  useGetPayrollDataByContractorQuery,
  useGetInvoiceSummariesQuery,
  useSavePayrollMutation,
} = withHarvestDataEndpoints;
